import { useCallback, useState } from "react";

import { staffErrorText, staffGet, staffPost, staffPut } from "../staff";
import type { StaffSession } from "../types";
import StaffGate from "./StaffGate";

/**
 * ECON-01 — Kernvel-internal cost/contribution reads (decided 2026-09-18).
 *
 * Deliberately NOT behind `<Protected>` / the AdminUser session (see App.tsx): no shop operator,
 * including an `owner`/`admin`, may ever see Kernvel's cost-to-serve them. The page is gated by the
 * Kernvel-staff authority (`StaffGate`, scope `econ01.read`) and reads
 * `/api/v1/staff/economics/{metric}` with the staff token; every read is audited server-side.
 * `shop_id` is a free-text field — Kernvel staff pick which shop to inspect.
 */

const METRICS = [
  { id: "cost-coverage", label: "Cost coverage" },
  { id: "billed-invoice-reconciliation", label: "Billed-to-invoice reconciliation" },
  { id: "budget-per-outcome", label: "Budget per outcome" },
  { id: "refund-adjusted-contribution", label: "Refund-adjusted contribution" },
  { id: "conversion-lift", label: "Conversion lift" },
  // Real cost-source completeness (product COGS / delivery / payment-processing): each order in
  // the window is observed/declared/missing per component; contribution is computed only when
  // every order in the window resolved all three -- never a guessed number. See
  // app/services/economics_cost_sources.py.
  { id: "cost-completeness/period", label: "Cost-source completeness (period)" },
] as const;
const PERIODS = ["7d", "30d", "90d", "365d"];

type MetricState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; data: unknown };

function MetricPanel({ metric, shopId, period }: { metric: (typeof METRICS)[number]; shopId: string; period: string }) {
  const [state, setState] = useState<MetricState>({ status: "idle" });

  const load = useCallback(async () => {
    if (!shopId.trim()) {
      setState({ status: "error", message: "Enter a shop_id first." });
      return;
    }
    setState({ status: "loading" });
    try {
      const query = `shop_id=${encodeURIComponent(shopId.trim())}&period=${encodeURIComponent(period)}`;
      const data = await staffGet(`/economics/${metric.id}?${query}`);
      setState({ status: "loaded", data });
    } catch (e) {
      setState({ status: "error", message: staffErrorText(e, "Request failed") });
    }
  }, [metric.id, shopId, period]);

  return (
    <div className="card">
      <div className="card-cust">{metric.label}</div>
      <button onClick={() => void load()} disabled={state.status === "loading"}>
        {state.status === "loading" ? "Loading…" : "Fetch"}
      </button>
      {state.status === "error" && <p className="risk-sub">Error: {state.message}</p>}
      {state.status === "loaded" && (
        <>
          <p className="risk-sub">Shop {shopId} · {period}</p>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8 }}>{JSON.stringify(state.data, null, 2)}</pre>
        </>
      )}
    </div>
  );
}

// ECON-01 per-shop cost-source opt-ins (`marketplace_settings.economics.cost_sources.*`, both default
// OFF). Reading needs econ01.read; toggling needs econ01.write. Every change is audited server-side
// (the staff audit event commits in the same transaction as the setting).
const COST_SOURCE_SETTINGS = [
  { key: "shopify_product_cost_sync", label: "Shopify product-cost sync (InventoryItem.unitCost)" },
  { key: "payment_processing_observed_sync", label: "Observed payment fees (OrderTransaction.fees, live call per order)" },
] as const;
type CostSourceKey = (typeof COST_SOURCE_SETTINGS)[number]["key"];
type CostSourceSettings = Record<CostSourceKey, boolean>;

function CostSourceSettingsPanel({ shopId, canWrite }: { shopId: string; canWrite: boolean }) {
  const [settings, setSettings] = useState<CostSourceSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = `shop_id=${encodeURIComponent(shopId)}`;

  const load = useCallback(async () => {
    if (!shopId) {
      setError("Enter a shop_id first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await staffGet<{ settings: CostSourceSettings }>(`/economics/cost-sources/settings?${query}`);
      setSettings(data.settings);
    } catch (e) {
      setError(staffErrorText(e, "Request failed"));
    } finally {
      setBusy(false);
    }
  }, [shopId, query]);

  const toggle = useCallback(
    async (key: CostSourceKey, value: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const data = await staffPut<{ after: CostSourceSettings }>(`/economics/cost-sources/settings?${query}`, {
          [key]: value,
        });
        setSettings(data.after);
      } catch (e) {
        setError(staffErrorText(e, "Update failed"));
      } finally {
        setBusy(false);
      }
    },
    [query],
  );

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-cust">Cost-source settings</div>
      <button onClick={() => void load()} disabled={busy}>
        {busy && settings === null ? "Loading…" : "Load settings"}
      </button>
      {error && <p className="risk-sub">Error: {error}</p>}
      {settings && (
        <div style={{ marginTop: 8 }}>
          {COST_SOURCE_SETTINGS.map((item) => (
            <label key={item.key} style={{ display: "block", marginTop: 4 }}>
              <input
                type="checkbox"
                checked={settings[item.key]}
                disabled={!canWrite || busy}
                onChange={(e) => void toggle(item.key, e.target.checked)}
              />{" "}
              {item.label}
            </label>
          ))}
          {!canWrite && <p className="cap-note">Read-only: your staff grant does not include econ01.write.</p>}
        </div>
      )}
    </div>
  );
}

// B6 — the refunded invoice case. A billing period's refunds are "known" only when a COMPLETE Admin readback of
// the app credits AND a staff attestation of Partner Dashboard refunds both reach the period's end; otherwise the
// reconciliation shows them as unknown (never 0). Every action here is audited server-side (econ01.write).
function RefundsPanel({ shopId, canWrite }: { shopId: string; canWrite: boolean }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [reference, setReference] = useState("");
  const [through, setThrough] = useState("");
  const [note, setNote] = useState("");

  const run = useCallback(async (label: string, call: () => Promise<unknown>) => {
    if (!shopId) {
      setMessage("Enter a shop_id first.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const data = await call();
      setMessage(`${label}: ${JSON.stringify(data)}`);
    } catch (e) {
      setMessage(`${label} failed: ${staffErrorText(e, "Request failed")}`);
    } finally {
      setBusy(false);
    }
  }, [shopId]);

  const cents = /^\d+$/.test(amount.trim()) ? Number(amount.trim()) : NaN;
  const iso = (value: string) => (value ? new Date(value).toISOString() : "");
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-cust">Refunded invoices (app credits and Partner refunds)</div>
      {!canWrite && <p className="cap-note">Refund actions need the econ01.write staff grant.</p>}
      <button disabled={!canWrite || busy} onClick={() => void run("Credit readback", () =>
        staffPost(`/economics/refunds/app-credits/sync?shop_id=${encodeURIComponent(shopId)}`, {}))}>
        Read back app credits (complete or nothing)
      </button>
      <fieldset style={{ marginTop: 8 }} disabled={!canWrite || busy}>
        <legend>Record a Partner Dashboard refund</legend>
        <label style={{ display: "block" }}>Amount (US cents)
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" /></label>
        <label style={{ display: "block" }}>Refunded at
          <input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} /></label>
        <label style={{ display: "block" }}>Partner transaction reference
          <input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
        <label style={{ display: "block" }}>Note (what and why)
          <input value={note} onChange={(e) => setNote(e.target.value)} /></label>
        <button disabled={!Number.isSafeInteger(cents) || cents <= 0 || !occurredAt || reference.trim().length < 4
          || note.trim().length < 8} onClick={() => void run("Partner refund", () => staffPost("/economics/refunds/partner", {
            shop_id: shopId, amount_minor: cents, occurred_at: iso(occurredAt),
            partner_transaction_ref: reference.trim(), note: note.trim() }))}>Record refund</button>
      </fieldset>
      <fieldset style={{ marginTop: 8 }} disabled={!canWrite || busy}>
        <legend>Attest Partner refunds are complete</legend>
        <label style={{ display: "block" }}>Every refund recorded through
          <input type="datetime-local" value={through} onChange={(e) => setThrough(e.target.value)} /></label>
        <button disabled={!through || note.trim().length < 8} onClick={() => void run("Attestation", () =>
          staffPost("/economics/refunds/attest", { shop_id: shopId, covered_through: iso(through), note: note.trim() }))}>
          Attest (uses the note above)</button>
      </fieldset>
      {message && <p className="risk-sub" style={{ whiteSpace: "pre-wrap" }}>{message}</p>}
    </div>
  );
}

function EconomicsBody({ canWrite = false }: { canWrite?: boolean }) {
  const [shopId, setShopId] = useState("");
  const [period, setPeriod] = useState("30d");
  const selectedShopId = shopId.trim();
  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <label style={{ display: "block" }}>
          Shop ID
          <input
            type="text"
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            style={{ display: "block", width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ display: "block", marginTop: 8 }}>
          Period
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ display: "block", marginTop: 4 }}>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>
      <CostSourceSettingsPanel key={`settings:${selectedShopId}`} shopId={selectedShopId} canWrite={canWrite} />
      <RefundsPanel key={`refunds:${selectedShopId}`} shopId={selectedShopId} canWrite={canWrite} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "10px" }}>
        {/* A selection change retires old responses and Loading. Old shop data must not sit under a new ID. */}
        {METRICS.map((metric) => (
          <MetricPanel key={`${metric.id}:${selectedShopId}:${period}`}
            metric={metric} shopId={selectedShopId} period={period} />
        ))}
      </div>
    </>
  );
}

export default function Economics() {
  return (
    <StaffGate title="Economics" scope="econ01.read">
      {(session: StaffSession) => <EconomicsBody canWrite={session.scopes.includes("econ01.write")} />}
    </StaffGate>
  );
}
